// @flow
import * as React from 'react';
import { Chip, Divider, Stack, Typography } from '@mui/material';
import {
  AgentCallAgendaWholeStateSubscription,
  AgentView, useAgentCallAgenda,
} from '../../../shared';
import { ArrowFatLeftIcon } from '@phosphor-icons/react';
import { RoundRobinLead } from '../../../features';
import { AgentAvailabilityStateOutput } from '@coverright/data-access/types/enrollment';
import _ from 'lodash';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

export function RoundRobinAgents() {
  const {state: data} = useAgentCallAgenda()

  const agents = data?.agentsSorted || [];
  const tasks = data?.tasks || [];

  const activeQueue = data?.rrView.inActiveQueue || []
  const busy = data?.rrView.busy || []
  const clientsWaiting = data?.rrView.tasksWaitingPool || [];

  return (
    <Stack direction={'row'} gap={5}>
      <Stack gap={2} flex={1}>
        <Typography  fontWeight={500}>Clients waiting for assignment {!!clientsWaiting.length && ` (${clientsWaiting.length})`}</Typography>
        <Divider />

        {!clientsWaiting.length && <Typography align={'center'}
                                            sx={{border: '1px solid #0000001f', p: 3, borderRadius: '8px'}}
                                            color={'#737373b3'}
                                            variant={'body2'}>
          No clients waiting
        </Typography>}

        <OverlayScrollbarsComponent
          style={{height: 'calc(100vh - 175px)', paddingRight: 16}}
          options={{ paddingAbsolute: true }}
          defer
        >
          <Stack gap={2} pt={1}>
            {clientsWaiting.map(client => tasks.find(t => client.clientId === t.clientId))
              .filter(Boolean)
              .map(client => (
                <RoundRobinLead client={client!} showTime key={client!.clientId} />
              ))}
          </Stack>
        </OverlayScrollbarsComponent>

      </Stack>
      <Stack gap={4} flex={2}>
        <Stack gap={2} minHeight={150}>
          <Typography  fontWeight={500}>Agent assignment queue</Typography>
          <Divider />
          {!activeQueue.length && <Typography align={'center'}
                                              sx={{border: '1px solid #0000001f', p: 3, borderRadius: '8px'}}
                                              color={'#737373b3'}
                                              variant={'body2'}>
            No agents in queue
          </Typography>}
          {activeQueue.map(id => agents.find(a => a.agentId === id))
            .filter(Boolean)
            .map(agent => (
              <AgentView key={agent!.agentId} agent={{ ...agent, email: agent!.agentEmail, fullName: agent!.agentName }} />
            ))}
        </Stack>
        <Stack gap={2}>
          <Typography fontWeight={500}>Busy</Typography>
          <Divider />

          {!busy.length && <Typography align={'center'}
                                              sx={{border: '1px solid #0000001f', p: 3, borderRadius: '8px'}}
                                              color={'#737373b3'}
                                              variant={'body2'}>
            No busy agents
          </Typography>}

          {busy.map(busyInfo => ({
            agent: agents.find(a => a.agentId === busyInfo.agentId),
            busyInfo
          }))
            .map(info => (
              <Stack direction={'row'} gap={4} key={info.busyInfo.agentId} alignItems={'center'}>
                <AgentView agent={{ email: info.agent?.agentEmail, picUrl: info.agent?.picUrl, fullName: info.agent?.agentName }} />
                  <Reason agent={info.agent} info={info.busyInfo} />
                {info.busyInfo.assignedToClient && <>
                  <ArrowFatLeftIcon size={32} color={'#70a6b6'} weight={'fill'} />
                  <RoundRobinLead client={tasks.find(t => t.currentAssignment?.assignedAgentId === info.busyInfo.agentId)!} />
                </>}
              </Stack>
            ))}
        </Stack>
      </Stack>
    </Stack>
  );
}

type ReasonProps = {
  agent?: AgentAvailabilityStateOutput,
  info: null | AgentCallAgendaWholeStateSubscription['agentCallAgendaWholeState']['rrView']['busy'][0]
}

const Reason = ({agent, info}: ReasonProps) => {
  let reason = '';
  let color: 'error' | 'info' = 'error';

  if (!info) return null;

  if (info.excluded) {
    reason = 'Excluded';
  } else if (info.dontDisturb) {
    reason = 'Don\'t Disturb';
  } else if (info.assignedToClient || !agent?.airCallAvailabilityStatus) {
    return null
  } else {
    reason = _.upperCase(agent?.airCallAvailabilityStatus);
    color = 'info';
  }

  return <Chip label={reason.toUpperCase()} sx={{fontSize: 12}} variant={'outlined'} color={color} size={'small'} />
}
